/* eslint-disable react/prop-types */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "../../layouts/PageTitle";
import config from "../../../../config.json";
import { getMenuLanguages } from "../../../constants/menuLanguages";
import {
  IMAGE_PICKER_ACCEPT,
  validateImageFile,
} from "../../../utils/imageValidation";
import {
  getCurrencySymbol,
  getRestaurantCurrencyCode,
} from "../../../utils/currency";
import CurrencyFlag from "../AppsMenu/AppProfile/CurrencyFlag";
import "../../../i18n";
import WizardLivePhone from "../Setup/StartNowWizard/WizardLivePhone";
import { buildItemFormLivePreview } from "../Cms/MenuTree/menuTreeUtils";
import "./AddCategory.css";

const EMPTY_LIVE_DRAFT = { visualSettingsMode: "custom" };

const AddItem = ({ onSuccess, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const languages = useMemo(() => getMenuLanguages(t), [t]);
  const embedded = Boolean(onSuccess || onClose);
  const fileInputRef = useRef(null);
  const currencyCode = getRestaurantCurrencyCode();

  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [price, setPrice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openLangs, setOpenLangs] = useState({ en: true });
  const [itemDetails, setItemDetails] = useState(
    languages.reduce((acc, lang) => {
      acc[lang.code] = { itemTitle: "" };
      return acc;
    }, {})
  );

  useEffect(() => () => imageUrl && URL.revokeObjectURL(imageUrl), [imageUrl]);

  const isLangOpen = (code) => Boolean(openLangs[code]);
  const toggleLang = (code) =>
    setOpenLangs((prev) => ({ ...prev, [code]: !prev[code] }));
  const openLanguages = languages.filter((lang) => isLangOpen(lang.code));

  const livePreview = useMemo(
    () =>
      buildItemFormLivePreview({
        titles: Object.fromEntries(
          Object.entries(itemDetails).map(([code, details]) => [
            code,
            details?.itemTitle || "",
          ])
        ),
      }),
    [itemDetails]
  );

  const handleLangInputChange = (lang, field, value) => {
    setItemDetails((prev) => ({
      ...prev,
      [lang]: {
        ...prev[lang],
        [field]: value,
      },
    }));
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    event.target.value = "";
    if (!selectedFile) return;

    const { isValid, message } = validateImageFile(selectedFile, 5);
    if (!isValid) {
      swal("Warning", message, "warning");
      return;
    }

    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setFile(selectedFile);
    setImageUrl(URL.createObjectURL(selectedFile));
  };

  const handleRemoveImage = () => {
    if (imageUrl) URL.revokeObjectURL(imageUrl);
    setImageUrl(null);
    setFile(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    if (!itemDetails || Object.keys(itemDetails).length === 0) {
      swal(t("warningTitle"), t("requiredItemsDetailes"), t("warning"));
      return;
    }

    const itemDetailsToSend = Object.entries(itemDetails)
      .map(([lang, details]) => ({
        language: lang,
        itemTitle: details.itemTitle?.trim(),
      }))
      .filter((detail) => detail.itemTitle);

    const englishEntry = itemDetailsToSend.find((detail) => detail.language === "en");
    if (!englishEntry) {
      swal(t("warningTitle"), t("englishDataRequired"), t("warning"));
      return;
    }

    if (price === "" || price === null || Number(price) < 0) {
      swal(t("warningTitle"), t("priceError"), t("warning"));
      return;
    }

    const formDataToSend = new FormData();
    formDataToSend.append(
      "data",
      JSON.stringify({
        itemDetails: itemDetailsToSend,
        price: Number(price),
      })
    );
    if (file) formDataToSend.append("file", file);

    setSubmitting(true);
    try {
      await axios.post(`${config.dev}/v2/items/create`, formDataToSend, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
        },
      });

      swal({
        title: t("successTitle"),
        text: t("itemCreated"),
        icon: "success",
        timer: 1000,
        buttons: false,
      }).then(() => {
        if (onSuccess) onSuccess();
        else navigate("/items");
      });
    } catch (error) {
      console.error("Erreur lors de l'ajout de l'item :", error.response?.data || error.message);
      swal(t("warningTitle"), t("updateFailed") || "Failed to add item.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const form = (
    <form
      className={`catform${embedded ? " catform--embed" : ""}`}
      onSubmit={handleSubmit}
      noValidate
    >
      {!embedded && (
        <div className="catform__top">
          <h1 className="catform__title">{t("itemFormTitle")}</h1>
          <button type="button" className="btn catform__link" onClick={() => navigate("/items")}>
            <i className="fas fa-arrow-left" aria-hidden /> {t("itemsList")}
          </button>
        </div>
      )}

      <section className="catform__section">
        <h2 className="catform__heading">
          {t("prodFormPhoto")} <span className="catform__optional">{t("optional")}</span>
        </h2>
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept={IMAGE_PICKER_ACCEPT}
          onChange={handleFileChange}
        />
        <div className="catform__photo">
          <button
            type="button"
            className="catform__thumb"
            onClick={() => fileInputRef.current?.click()}
          >
            {imageUrl ? <img src={imageUrl} alt="" /> : <i className="fas fa-camera" aria-hidden />}
          </button>
          <div className="catform__photo-actions">
            <button
              type="button"
              className="btn catform__small"
              onClick={() => fileInputRef.current?.click()}
            >
              {imageUrl ? t("catFormReplacePhoto") : t("catFormChoosePhoto")}
            </button>
            {imageUrl && (
              <button
                type="button"
                className="btn catform__small is-danger"
                onClick={handleRemoveImage}
              >
                {t("catFormRemovePhoto")}
              </button>
            )}
          </div>
        </div>
      </section>

      <section className="catform__section">
        <h2 className="catform__heading">{t("catFormStepName")}</h2>
        <p className="catform__pills-label">{t("itemFormChooseLanguage")}</p>
        <div className="catform__pills">
          {languages.map((lang) => {
            const filled = Boolean(itemDetails[lang.code]?.itemTitle?.trim());
            return (
              <button
                key={lang.code}
                type="button"
                className={`catform__pill ${isLangOpen(lang.code) ? "is-open" : ""} ${filled ? "is-filled" : ""}`}
                aria-pressed={isLangOpen(lang.code)}
                onClick={() => toggleLang(lang.code)}
              >
                <img src={lang.flag} alt="" />
                {lang.label}
                {filled && <i className="fas fa-check" aria-hidden />}
              </button>
            );
          })}
        </div>
        <div className="catform__lang-fields">
          {openLanguages.map((lang) => (
            <div className="catform__lang-panel" key={lang.code}>
              <div className="catform__field">
                <label className="catform__label" htmlFor={`item-title-${lang.code}`}>
                  <img src={lang.flag} alt="" />
                  {lang.label} — {t("title")}
                </label>
                <input
                  id={`item-title-${lang.code}`}
                  type="text"
                  className="catform__input"
                  value={itemDetails[lang.code]?.itemTitle || ""}
                  autoFocus={lang.code === "en"}
                  dir={lang.code === "ar" ? "rtl" : "ltr"}
                  onChange={(e) => handleLangInputChange(lang.code, "itemTitle", e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="catform__section">
        <h2 className="catform__heading">{t("prodFormPrice")}</h2>
        <div className="catform__field">
          <label className="catform__label" htmlFor="item-price">
            {t("price")}
            {currencyCode && (
              <span className="catform__currency">
                <CurrencyFlag code={currencyCode} className="currency-flag--inline" />
                {currencyCode} {getCurrencySymbol(currencyCode)}
              </span>
            )}
          </label>
          <input
            id="item-price"
            type="number"
            min={0}
            step="0.01"
            className="catform__input"
            value={price}
            placeholder="0.00"
            onChange={(e) => {
              const value = e.target.value.replace(",", ".");
              if (value === "" || (!Number.isNaN(Number(value)) && Number(value) >= 0)) {
                setPrice(value);
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "-" || e.key === "e") e.preventDefault();
            }}
          />
        </div>
      </section>

      <div className="catform__actions">
        <button
          type="button"
          className="btn catform__btn catform__btn--cancel"
          onClick={() => (embedded ? onClose?.() : navigate("/items"))}
        >
          {t("cancel")}
        </button>
        <button type="submit" className="btn catform__btn catform__btn--primary" disabled={submitting}>
          {submitting ? t("catFormCreating") : t("addItem")}
        </button>
      </div>
    </form>
  );

  const preview = (
    <aside className="catform-live" aria-label={t("wizardPreviewLive")}>
      <WizardLivePhone
        draft={EMPTY_LIVE_DRAFT}
        compact
        showStepLabel={false}
        productDetail={livePreview}
      />
    </aside>
  );

  const layout = (
    <div className="catform-with-preview">
      {form}
      {preview}
    </div>
  );

  if (embedded) return layout;

  return (
    <>
      <PageTitle activeMenu={t("addItem")} motherMenu={t("kitchen")} />
      {layout}
    </>
  );
};

export default AddItem;
